import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { CookieService } from 'ngx-cookie-service';
import { ICobrandDetails } from '@data/admin/cobrand-details';
import { environment } from "@env/environment";
import { IUser } from '@data/user-profile/user-profile.interface';
import { AuthToken } from './auth-token.interface';

type AuthCookieOptions = {
  path: '/';
  secure: true;
  sameSite: 'Strict';
  domain?: string;
};

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private data = new BehaviorSubject(null);
  private laAuth: AuthToken | null = null;

  public authUser = this.data.asObservable();
  public cobrandDetails$ = new Subject<ICobrandDetails>();
  public userDetails$ = new BehaviorSubject<IUser | null>(null);

  public account_id!: string;
  public user_details!: IUser;

  constructor(
    private _cookieService: CookieService
  ) {}

  // Start of User Cookie functions
  public setCookie(useSharedCookieDomain = true): void {
    const laAuthMimicCookie = this._cookieService.get('LAAuth2LAWebAdmin');
    const laAuthTransitionCookie = this._cookieService.get('LAAuth2LAWeb');
    let laWebCookie = this._cookieService.get('LAWEB');

    // console.debug('no auth cookie: ' + decodeURIComponent(laAuthTransitionCookie));
    // console.debug('current auth cookie: ' + decodeURIComponent(laWebCookie));

    //TODO: remove the debug token
    // laWebCookie = (window.location.href.includes("localhost"))
    //   ? {
    //       "access_token": "2a10giTYklX7mbVIGATh3XuMOVaCMp3y8LIqjw6ELZV5VCfKhi",
    //       "expires_in": 1200,
    //       "token_type": "Bearer",
    //       "user_id": 10973,
    //       "refresh_token": "2a10HSx3SFIBCnYGjNKR2782y8ckDsBpdHETF7LTGdWVaaX1Lg",
    //       "context": "LApro"
    //     }.toString()
    //   : laWebCookie;

    if (laAuthMimicCookie.length > 0) // Mimic Admin logged in
      this.updateMimicCookie(JSON.parse(decodeURIComponent(laAuthMimicCookie)), useSharedCookieDomain);

    if (laAuthTransitionCookie.length > 0) // Just logged in
      this.setToken(JSON.parse(decodeURIComponent(laAuthTransitionCookie)), useSharedCookieDomain);

    else if (laWebCookie.length > 0) // Reloaded the page (refresh or opened the browser)
      this.setToken(JSON.parse(decodeURIComponent(laWebCookie)), useSharedCookieDomain);

    this._cookieService.delete('LAAuth2LAWeb', '/', '.leadadvantagepro.com'); // Always attempt to delete the LAAuth2LAWeb

    if (this.laAuth === null) // If nothing was set, no LAWEB nor LAAuth2LAWeb cookies found, kick back to LA-Auth
      this.killCookie()

  }

  public setToken(token: AuthToken, useSharedCookieDomain = true): void {
    //delete current auth token first
    this._deleteAuthCookie();

    this._cookieService.set(
      'LAWEB', encodeURIComponent(JSON.stringify(token)),
      this._getCookieOptions(useSharedCookieDomain)
    );

    this.laAuth = { ...token };
  }

  public updateMimicCookie(token: AuthToken, useSharedCookieDomain = true): void {
    this.deleteMimicAdminCookie();
    this._cookieService.set(
      'LAAuth2LAWebAdmin', encodeURIComponent(JSON.stringify(token)),
      this._getCookieOptions(useSharedCookieDomain)
    );
  }

  public getToken(): any {
    return this.laAuth;
  }

  public killCookie(): void {
    this._deleteAuthCookie();
    this.deleteMimicAdminCookie();
    this.redirectToAuth();
  }

  public redirectToAuth(): void {
    window.location.href = environment.authClient + '?referrer=' + encodeURIComponent(window.location.href);
  }

  public getAuthCookie(): string {
    return this._cookieService.get('LAWEB');
  }

  private _deleteAuthCookie() {
    this._cookieService.delete('LAWEB', '/', '.leadadvantagepro.com', true); // delete legacy cookie
    const domain = this._getCookieDomain();
    if (domain) {
      this._cookieService.delete('LAWEB', '/', domain, true);
    }
    this._cookieService.delete('LAWEB', '/'); // delete environment specific cookie
  }

  private _getCookieOptions(useSharedCookieDomain = true): AuthCookieOptions {
    const domain = useSharedCookieDomain ? this._getCookieDomain() : undefined;

    return domain
      ? { path: '/', domain, secure: true, sameSite: 'Strict' }
      : { path: '/', secure: true, sameSite: 'Strict' };
  }

  /**
   * Returns the shared cookie domain for the current environment so apex and
   * www hosts can share authentication without sharing cookies across environments.
   */
  private _getCookieDomain(): string | undefined {
    const hostname = window.location.hostname.toLowerCase();

    if (hostname === 'leadadvantagepro.com' || hostname === 'www.leadadvantagepro.com') {
      return '.leadadvantagepro.com';
    }

    // Share cookies between the apex and www hosts within a lower environment,
    // but never across environments.
    const environment = ['dev', 'beta', 'preprod']
      .find(name => hostname === `${name}.leadadvantagepro.com`
        || hostname === `www.${name}.leadadvantagepro.com`
        || hostname.endsWith(`.${name}.leadadvantagepro.com`));

    if (environment) {
      return `.${environment}.leadadvantagepro.com`;
    }

    return undefined;
  }

  public deleteMimicAdminCookie() {
    this._cookieService.delete('LAAuth2LAWebAdmin', '/');
    this._cookieService.delete('LAAuth2LAWebAdmin', '/', '.leadadvantagepro.com', true);
    const domain = this._getCookieDomain();
    if (domain && domain !== '.leadadvantagepro.com') {
      this._cookieService.delete('LAAuth2LAWebAdmin', '/', domain, true);
    }
  }
}
